import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { followersApi } from '../../api/followers.api';
import type { FollowedTrip } from '../../types';
import { useDateLocale } from '../../utils/dateLocale';
import { Bell, Icon } from '../../components/ui/Icon';

/**
 * ADR-0044: trips this account follows through their link. They open the
 * shared page — a follower never enters the trip itself.
 */
export default function FollowedTripsSection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dateLocale = useDateLocale();
  const [trips, setTrips] = useState<FollowedTrip[]>([]);

  useEffect(() => {
    followersApi.followedTrips().then(setTrips).catch(() => setTrips([]));
  }, []);

  if (trips.length === 0) return null;

  return (
    <section className="mt-10">
      <div className="flex items-center gap-2.5 mb-4">
        <Icon as={Bell} size={12} className="text-teal-500" />
        <span className="font-mono text-[10px] tracking-[0.16em] uppercase font-semibold text-slate-400 dark:text-slate-500">
          {t('dashboard.sections.following')}
        </span>
        <div className="flex-1 h-px bg-slate-200 dark:bg-slate-800" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5">
        {trips.map(trip => (
          <button
            key={trip.shareToken}
            onClick={() => navigate(`/share/${trip.shareToken}`)}
            className="text-left card p-4 hover:shadow-md transition-shadow cursor-pointer"
          >
            <p className="font-serif italic font-semibold text-lg text-slate-900 dark:text-slate-100 truncate">{trip.name}</p>
            <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
              {trip.destination} · {format(parseISO(trip.startDate.slice(0, 10)), 'd MMM', { locale: dateLocale })} – {format(parseISO(trip.endDate.slice(0, 10)), 'd MMM yyyy', { locale: dateLocale })}
            </p>
          </button>
        ))}
      </div>
    </section>
  );
}
